import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';

const EMPTY = {
  matricule: '', entryDate: '', fullName: '', birthDate: '',
  originProvinceRegion: '', cnibNumber: '', nationality: 'Burkinabé',
  cnssNumber: '', exitDate: '', exitReason: '',
};

export default function Registre() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState('');

  function load() {
    api.get('/staff').then((data) => {
      const list = (data || []).map((s) => ({
        id: s.id,
        matricule: s.matricule,
        entryDate: s.hireDate,
        fullName: `${s.lastName} ${s.firstName}`,
        birthDate: s.birthDate || '',
        originProvinceRegion: s.birthPlace || '',
        cnibNumber: s.cnib || '',
        nationality: 'Burkinabé',
        cnssNumber: s.cnss || '',
        exitDate: s.archivedAt || '',
        exitReason: '',
        status: s.status,
      }));
      setRows(list);
    }).catch((e) => setError(e.message));
  }

  useEffect(load, []);

  const filtered = rows.filter((r) =>
    !search || r.fullName.toLowerCase().includes(search.toLowerCase()) || r.matricule.toLowerCase().includes(search.toLowerCase())
  );

  const actifs = filtered.filter((r) => !r.exitDate);
  const sortis = filtered.filter((r) => r.exitDate);

  const d = (v) => v ? new Date(v).toLocaleDateString('fr-FR') : '—';

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi navy"><span>Total inscrits</span><strong>{rows.length}</strong></div>
        <div className="kpi green"><span>Actifs</span><strong>{rows.filter((r) => !r.exitDate).length}</strong></div>
        <div className="kpi"><span>Sortis</span><strong>{rows.filter((r) => r.exitDate).length}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Registre employeur</h2>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              className="search-input"
              style={{ width: 220 }}
              placeholder="Nom ou matricule…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="btn btn-sm" onClick={() => modal ? setModal(null) : setModal({ ...EMPTY })}>
              {modal ? 'Fermer' : '+ Nouvelle entrée'}
            </button>
          </div>
        </div>

        {modal && (
          <div className="panel-body" style={{ borderBottom: '1px solid var(--border)' }}>
            <RegistreForm
              initial={modal}
              onSaved={() => { setModal(null); load(); }}
              onCancel={() => setModal(null)}
            />
          </div>
        )}

        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}

          <h3 style={{ marginBottom: 8, fontSize: '0.95rem', color: 'var(--muted)' }}>Personnel actif ({actifs.length})</h3>
          <RegistreTable rows={actifs} d={d} onEdit={(r) => setModal(r)} />

          {sortis.length > 0 && (
            <>
              <h3 style={{ margin: '20px 0 8px', fontSize: '0.95rem', color: 'var(--muted)' }}>Personnel sorti ({sortis.length})</h3>
              <RegistreTable rows={sortis} d={d} onEdit={(r) => setModal(r)} />
            </>
          )}
        </div>
      </div>
    </>
  );
}

function RegistreTable({ rows, d, onEdit }) {
  if (rows.length === 0) return <div className="empty">Aucune entrée.</div>;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Matricule</th>
            <th>Date entrée</th>
            <th>Nom & Prénoms</th>
            <th>Date naissance</th>
            <th>Province/Région</th>
            <th>N° CNIB</th>
            <th>Nationalité</th>
            <th>N° CNSS</th>
            <th>Date sortie</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="mono"><b>{r.matricule}</b></td>
              <td className="mono">{d(r.entryDate)}</td>
              <td><b>{r.fullName}</b></td>
              <td className="mono">{d(r.birthDate)}</td>
              <td>{r.originProvinceRegion || '—'}</td>
              <td className="mono">{r.cnibNumber || '—'}</td>
              <td>{r.nationality || '—'}</td>
              <td className="mono">{r.cnssNumber || '—'}</td>
              <td className="mono">{r.exitDate ? d(r.exitDate) : <span style={{ color: 'var(--success)' }}>En poste</span>}</td>
              <td>
                <button className="btn-ghost btn-sm" onClick={() => onEdit(r)}>Modifier</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RegistreForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  async function save() {
    if (!f.matricule || !f.fullName) { setError('Matricule et nom sont obligatoires.'); return; }
    setSaving(true);
    try {
      const [lastName, ...rest] = f.fullName.trim().split(' ');
      const firstName = rest.join(' ');
      const payload = {
        matricule: f.matricule,
        lastName: lastName || f.fullName,
        firstName: firstName || '',
        hireDate: f.entryDate || undefined,
        birthDate: f.birthDate || undefined,
        birthPlace: f.originProvinceRegion || undefined,
        cnib: f.cnibNumber || undefined,
        cnss: f.cnssNumber || undefined,
      };
      if (initial.id) {
        await api.patch(`/staff/${initial.id}`, payload);
      } else {
        await api.post('/staff', { ...payload, type: 'ADMINISTRATIF' });
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h3 style={{ marginBottom: 12 }}>{initial.id ? 'Modifier l\'entrée' : 'Nouvelle entrée au registre'}</h3>
      {error && <div className="form-error">{error}</div>}
      <div className="grid-3">
        <div className="field"><label>Matricule *</label><input value={f.matricule} onChange={set('matricule')} /></div>
        <div className="field"><label>Date d'entrée *</label><input type="date" value={f.entryDate ? f.entryDate.slice(0, 10) : ''} onChange={set('entryDate')} /></div>
        <div className="field"><label>Nom & Prénoms *</label><input value={f.fullName} onChange={set('fullName')} /></div>
        <div className="field"><label>Date de naissance</label><input type="date" value={f.birthDate ? f.birthDate.slice(0, 10) : ''} onChange={set('birthDate')} /></div>
        <div className="field"><label>Province & Région d'origine</label><input value={f.originProvinceRegion} onChange={set('originProvinceRegion')} /></div>
        <div className="field"><label>N° CNIB</label><input value={f.cnibNumber} onChange={set('cnibNumber')} /></div>
        <div className="field"><label>Nationalité</label><input value={f.nationality} onChange={set('nationality')} /></div>
        <div className="field"><label>N° CNSS</label><input value={f.cnssNumber} onChange={set('cnssNumber')} /></div>
        <div className="field"><label>Date de sortie</label><input type="date" value={f.exitDate ? f.exitDate.slice(0, 10) : ''} onChange={set('exitDate')} /></div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
          {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button className="btn-ghost" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}
